import React, { useEffect, useState } from 'react'
import Button from '~/components/Button'
import { Close, Expand } from '~/components/Icons'
import { Portal } from '~/components/utils'
import type { HTMLElementProps, Image } from '~/types'
import { cn, imageUrl, useDominantColor } from '~/utils'
import { Slideshow } from './Slideshow'

type Props = HTMLElementProps<
	'div',
	{
		cover?: Image
		images: Array<Pick<Image, 'id' | 'url' | 'altText'>>
		mode?: 'inline' | 'dialog' | 'both'
		thumbnails?: boolean
		/** Images to preload to increase load times when they appear */
		preloadedImages?: Array<Image | string>
	}
>

export const Gallery: React.FC<Props> = ({
	className,
	mode = 'dialog',
	thumbnails = true,
	images: _images,
	cover = _images[0],
	preloadedImages = [],
	...props
}) => {
	const images = cover.id !== _images[0].id ? [cover, ..._images] : _images

	const [open, setOpen] = useState<number | null>(null)
	const [inlineIndex, __setInlineIndex] = useState<number>(0)
	const setInlineIndex = (n: number) => __setInlineIndex(n >= images.length ? 0 : n < 0 ? images.length - 1 : n)
	useEffect(() => __setInlineIndex(0), [cover.url])

	const controlsColor = useDominantColor(images[inlineIndex].url, { height: 400, width: 100, crop: 'bottom' })
	const GalleryClassNames = 'cursor-pointer overflow-hidden'
	// Don't render when there are no images
	return cover === undefined ? null : (
		<>
			{mode == 'inline' || mode == 'both' ? (
				<Slideshow
					{...props}
					className={cn(GalleryClassNames, className)}
					onClick={_ => setInlineIndex(inlineIndex + 1)}
					onUpdate={setInlineIndex}
					index={inlineIndex}
					background="cover"
					images={images}
					controlsColor={controlsColor ?? undefined}
				>
					{mode == 'both' && (
						<Expand
							className={cn(
								'absolute bottom-0 right-0 z-20 box-content p-6 text-3xl',
								images.length > 1 ? 'opacity-70 hover:opacity-100' : 'opacity-20 hover:opacity-40',
								controlsColor
							)}
							onClick={e => {
								e.stopPropagation()
								setOpen(inlineIndex)
							}}
						/>
					)}
				</Slideshow>
			) : (
				<div {...props} className={cn(GalleryClassNames, 'relative mb-4 rounded-3xl', className)}>
					<div
						role="img"
						style={{ backgroundImage: `url(${cover.url})` }}
						aria-label={cover?.altText || undefined}
						className={
							'absolute h-full w-full bg-cover bg-center ' +
							'scale-100 transition-transform duration-300 ease-out hover:scale-105'
						}
						onClick={_ => setOpen(0)}
					/>
					<Expand className="pointer-events-none absolute bottom-4 right-4 h-6" fill="white" />
				</div>
			)}
			{thumbnails && images.length > 1 && (
				<div className="mt-2 grid grid-cols-[repeat(auto-fit,5rem)] gap-2">
					{images.map((media, i) =>
						mode == 'dialog' && i === 0 ? null : ( // Hide cover image from list when in dialog mode
							<button
								key={i}
								className="button h-[5rem] rounded bg-cover bg-center"
								style={{ backgroundImage: `url(${imageUrl(media, 100)})` }}
								title={media.altText || undefined}
								onClick={_ => (mode == 'dialog' ? setOpen(i) : setInlineIndex(i))}
							/>
						)
					)}
				</div>
			)}
			<Portal open={open !== null && mode !== 'inline'}>
				<div className="fixed inset-0 z-50 bg-white">
					<dialog open className="relative h-full w-full overflow-hidden border-none bg-white p-0">
						<Slideshow
							index={open as number}
							images={images}
							onKeyDown={e => e.key == 'Escape' && setOpen(null)}
							onUpdate={n => {
								setInlineIndex(n)
								setOpen(n)
							}}
							onTripleClick={_ => setOpen(null)}
						>
							<Button
								className="absolute right-8 top-8 z-10 h-16 w-16 leading-none"
								onClick={_ => setOpen(null)}
							>
								<Close />
							</Button>
						</Slideshow>
					</dialog>
				</div>
			</Portal>
			{preloadedImages.map((img, i) => (
				<img key={i} className="absolute hidden" src={imageUrl(img)} />
			))}
		</>
	)
}
